import React, { useState, useEffect } from "react";
import {
	Button,
	Card,
	Form,
	Input,
	Modal,
	Table,
	Space,
	message,
	Popconfirm,
	Row,
	Col,
	Select,
	Typography,
	Empty,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
	requestAddAddress,
	requestDeleteAddress,
	requestGetAddressList,
	requestUpdateAddress,
} from "../../../services/user.service";
import { PiMapPinDuotone, PiPencilDuotone, PiPlusDuotone, PiTrashDuotone } from "react-icons/pi";

const { Title } = Typography;
const { Option } = Select;

const AddressTab = () => {
	const [addresses, setAddresses] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [editingAddress, setEditingAddress] = useState(null);
	const [form] = Form.useForm();

	// Country options (you can expand this list)
	const countryOptions = [
		{ code: "ID", name: "Indonesia" },
		{ code: "MY", name: "Malaysia" },
		{ code: "SG", name: "Singapore" },
		{ code: "TH", name: "Thailand" },
		{ code: "PH", name: "Philippines" },
		{ code: "VN", name: "Vietnam" },
		{ code: "US", name: "United States" },
		{ code: "GB", name: "United Kingdom" },
		{ code: "AU", name: "Australia" },
	];

	useEffect(() => {
		fetchAddresses();
	}, []);

	const fetchAddresses = async () => {
		setLoading(true);
		try {
			const response = await requestGetAddressList();
			if (response.data && response.data.data) {
				setAddresses(response.data.data);
			}
		} catch (error) {
			message.error("Gagal memuat daftar alamat");
			console.error("Error fetching addresses:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleAddAddress = () => {
		setEditingAddress(null);
		form.resetFields();
		// Set default country to Indonesia
		form.setFieldsValue({ countryCode: "ID", country: "Indonesia" });
		setModalVisible(true);
	};

	const handleEditAddress = (address) => {
		setEditingAddress(address);
		form.setFieldsValue(address);
		setModalVisible(true);
	};

	const handleDeleteAddress = async (addressId) => {
		try {
			await requestDeleteAddress(addressId);
			message.success("Alamat berhasil dihapus");
			fetchAddresses();
		} catch (error) {
			message.error("Gagal menghapus alamat");
			console.error("Error deleting address:", error);
		}
	};

	const handleSubmit = async (values) => {
		try {
			if (editingAddress) {
				await requestUpdateAddress(editingAddress.id, values);
				message.success("Alamat berhasil diperbarui");
			} else {
				await requestAddAddress(values);
				message.success("Alamat berhasil ditambahkan");
			}

			setModalVisible(false);
			form.resetFields();
			fetchAddresses();
		} catch (error) {
			message.error(editingAddress ? "Gagal memperbarui alamat" : "Gagal menambahkan alamat");
			console.error("Error saving address:", error);
		}
	};

	const handleCountryChange = (countryCode) => {
		const selectedCountry = countryOptions.find((c) => c.code === countryCode);
		if (selectedCountry) {
			form.setFieldsValue({
				countryCode: countryCode,
				country: selectedCountry.name,
			});
		}
	};

	return (
		<div>
			<div
				style={{
					marginBottom: 16,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}>
				<Title level={5} style={{ margin: 0 }}>
					Daftar Alamat
				</Title>
				<Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddAddress}>
					Tambah Alamat
				</Button>
			</div>

			{addresses && addresses.length > 0 ? (
				<div style={{ display: "grid", gap: 16 }}>
					{addresses.map((address, index) => (
						<Card
							key={address._id || index}
							size="small"
							extra={
								<Space>
									<Button
										type="text"
										size="small"
										icon={<PiPencilDuotone />}
										onClick={() => handleEditAddress(address)}
									/>
									<Popconfirm
										title="Delete Address"
										description="Are you sure you want to delete this address?"
										onConfirm={() => handleDeleteAddress(address._id)}
										okText="Yes"
										cancelText="No">
										<Button type="text" danger size="small" icon={<PiTrashDuotone />} />
									</Popconfirm>
								</Space>
							}>
							<div style={{ display: "flex", alignItems: "start", gap: 12 }}>
								<PiMapPinDuotone style={{ fontSize: 16, color: "#666", marginTop: 2 }} />
								<div style={{ flex: 1 }}>
									<div style={{ marginBottom: 4 }}>
										<Typography.Text strong style={{ fontSize: 14 }}>
											{address.label}
										</Typography.Text>
									</div>
									<div style={{ marginBottom: 4 }}>
										<Typography.Text style={{ fontSize: 13, color: "#666" }}>
											{address.name} • {address.email} • {address.phone}
										</Typography.Text>
									</div>
									<div style={{ fontSize: 13, color: "#333" }}>
										{address.street1}
										{address.street2 && `, ${address.street2}`}, {address.city}, {address.state}{" "}
										{address.postalCode}, {address.country}
									</div>
								</div>
							</div>
						</Card>
					))}
					<Button type="dashed" icon={<PiPlusDuotone />} onClick={handleAddAddress}>
						Tambah Alamat Baru
					</Button>
				</div>
			) : (
				<Empty
					description="No addresses found for this customer"
					image={<PiMapPinDuotone style={{ fontSize: 48, color: "#d9d9d9" }} />}>
					<Button type="primary" icon={<PiPlusDuotone />} onClick={handleAddAddress}>
						Tambah Alamat Pertama
					</Button>
				</Empty>
			)}

			<Modal
				title={editingAddress ? "Edit Alamat" : "Tambah Alamat Baru"}
				open={modalVisible}
				width={800}
				footer={[
					<Space>
						<Button
							onClick={() => {
								setModalVisible(false);
								form.resetFields();
							}}>
							Batal
						</Button>
						<Button
							type="primary"
							onClick={() => {
								form
									.validateFields()
									.then((values) => {
										if (values.phone && !values.phone.startsWith("+")) {
											values.phone = "+" + values.phone;
										}
										handleSubmit(values);
									})
									.catch((info) => {
										console.log("Validate Failed:", info);
									});
							}}>
							{editingAddress ? "Perbarui" : "Simpan"}
						</Button>
					</Space>,
				]}>
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						name: "",
						email: "",
						phone: "",
						label: "",
						street1: "",
						street2: "",
						city: "",
						state: "",
						postalCode: "",
						country: "Indonesia",
						countryCode: "ID",
					}}>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="name"
								label="Nama Lengkap"
								rules={[{ required: true, message: "Silakan masukkan nama lengkap" }]}>
								<Input placeholder="Masukkan nama lengkap" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="email"
								label="Email"
								rules={[
									{ required: true, message: "Silakan masukkan email" },
									{ type: "email", message: "Silakan masukkan email yang valid" },
								]}>
								<Input placeholder="Masukkan alamat email" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="phone"
								label="No.Telpon"
								rules={[{ required: true, message: "Silakan masukkan nomor telepon" }]}>
								<Input placeholder="Masukkan nomor telepon (contoh: +628123456789)" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="label"
								label="Label Alamat"
								rules={[{ required: true, message: "Silakan masukkan label alamat" }]}>
								<Select placeholder="Pilih label alamat">
									<Option value="Home">Rumah</Option>
									<Option value="Office">Kantor</Option>
									<Option value="Billing">Tagihan</Option>
									<Option value="Shipping">Pengiriman</Option>
									<Option value="Other">Lainnya</Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="street1"
								label="Alamat Jalan 1"
								rules={[{ required: true, message: "Silakan masukkan alamat jalan" }]}>
								<Input placeholder="Masukkan alamat jalan" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="street2" label="Alamat Jalan 2 (Opsional)">
								<Input placeholder="Masukkan informasi alamat tambahan" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={8}>
							<Form.Item
								name="city"
								label="Kota"
								rules={[{ required: true, message: "Silakan masukkan kota" }]}>
								<Input placeholder="Masukkan kota" />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="state"
								label="Provinsi"
								rules={[{ required: true, message: "Silakan masukkan provinsi" }]}>
								<Input placeholder="Masukkan provinsi" />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="postalCode"
								label="Kode Pos"
								rules={[{ required: true, message: "Silakan masukkan kode pos" }]}>
								<Input placeholder="Masukkan kode pos" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="country"
								label="Negara"
								rules={[{ required: true, message: "Silakan pilih negara" }]}>
								<Select placeholder="Pilih negara" showSearch>
									<Option value="Indonesia">Indonesia</Option>
									<Option value="Malaysia">Malaysia</Option>
									<Option value="Singapore">Singapore</Option>
									<Option value="Thailand">Thailand</Option>
									<Option value="Philippines">Filipina</Option>
									<Option value="Vietnam">Vietnam</Option>
									<Option value="Other">Lainnya</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="countryCode"
								label="Kode Negara"
								rules={[{ required: true, message: "Silakan masukkan kode negara" }]}>
								<Select placeholder="Pilih kode negara" showSearch>
									<Option value="ID">ID - Indonesia</Option>
									<Option value="MY">MY - Malaysia</Option>
									<Option value="SG">SG - Singapore</Option>
									<Option value="TH">TH - Thailand</Option>
									<Option value="PH">PH - Filipina</Option>
									<Option value="VN">VN - Vietnam</Option>
									<Option value="US">US - Amerika Serikat</Option>
									<Option value="AU">AU - Australia</Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Modal>
		</div>
	);
};

export default AddressTab;
